import axios from 'axios';

const API_URL = '/api';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
// IMPORTANT: do NOT nuke the token on 401s from the login/register endpoints
// (bad password = 401, must not log out the existing user) or from monitoring
// / metrics endpoints (transient 5xx shouldn't cascade into a forced logout
// for everyone sharing this browser). Only force-logout on 401s from endpoints
// that the user is currently authenticated against.
//
// Belt-and-suspenders against the "kicked out every ~5 min" pattern: only
// redirect to /login after we see CONSECUTIVE 401s from /auth/me, not a
// single transient one. A single 401 might be a momentary blip — wait for
// the next 60s probe to confirm before kicking the user out.
let _consecutiveAuthMeFailures = 0;
const _CONSECUTIVE_AUTH_ME_FAILURES_TO_LOGOUT = 3;
api.interceptors.response.use(
  (response) => {
    // Any successful response resets the failure counter
    _consecutiveAuthMeFailures = 0;
    return response;
  },
  (error) => {
    const url = error.config?.url || '';
    const method = (error.config?.method || 'get').toLowerCase();
    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register') || url.includes('/auth/change-password');
    const isMonitoringEndpoint = url.includes('/monitoring/') || url.includes('/metrics');

    if (error.response?.status === 401 && !isAuthEndpoint && !isMonitoringEndpoint) {
      const isAuthMe = url.includes('/auth/me');
      if (isAuthMe) {
        _consecutiveAuthMeFailures++;
        if (_consecutiveAuthMeFailures >= _CONSECUTIVE_AUTH_ME_FAILURES_TO_LOGOUT) {
          // Belt-and-suspenders: if the server still has a valid session cookie,
          // DON'T nuke localStorage — rehydrate from cookie and let AuthContext
          // pick it up on next mount. This handles the case where localStorage
          // was cleared (browser devtools, another tab, private window sharing
          // cookies but not localStorage) but the user is still authenticated.
          const cookieToken = document.cookie.match(/(?:^|;\s*)token=([^;]+)/)?.[1];
          const lsToken = localStorage.getItem('token');
          if (cookieToken && !lsToken) {
            try { localStorage.setItem('token', cookieToken); } catch (_) {}
            _consecutiveAuthMeFailures = 0; // give it a chance
            console.warn(`[auth] /auth/me 401 (${_consecutiveAuthMeFailures}/${_CONSECUTIVE_AUTH_ME_FAILURES_TO_LOGOUT}) but cookie present and localStorage empty — rehydrated from cookie, NOT logging out`);
            return Promise.reject(error);
          }
          console.warn(`[auth] /auth/me returned 401 (${_consecutiveAuthMeFailures} in a row) — session is dead, logging out`);
          try { localStorage.removeItem('token'); } catch (_) {}
          window.location.href = '/login';
        } else {
          console.warn(`[auth] /auth/me returned 401 (${_consecutiveAuthMeFailures}/${_CONSECUTIVE_AUTH_ME_FAILURES_TO_LOGOUT}) — keeping session, will retry on next probe`);
        }
      } else {
        // Surface a one-off 401 to the page (e.g. "permission changed for
        // this resource") but don't kick the user out for a single bad
        // response. The /auth/me heartbeat will catch a truly dead session.
        console.warn('[auth] 401 from', method.toUpperCase(), url, '— NOT logging out (only /auth/me can)');
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const auth = {
  login: (username, password) => api.post('/auth/login', { username, password }),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
  // Arcade admin (proxied server-side to avoid CORS)
  getPendingUsers: () => api.get('/api/admin/pending-users'),
  approveUser: (id) => api.post(`/api/admin/approve/${id}`)
};

// Users
export const users = {
  list: () => api.get('/users'),
  get: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  resetPassword: (id, data = {}) => api.post(`/users/${id}/reset-password`, data),
  reactivate: (id) => api.post(`/users/${id}/reactivate`),
  changePassword: (data) => api.post('/users/change-password', data),
  updateProfile: (data) => api.put('/users/me', data),
  me: () => api.get('/users/me')
};

// Customers
export const customers = {
  list: (params) => api.get('/customers', { params }),
  get: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`)
};

// Entries
export const entries = {
  list: (params) => api.get('/entries', { params }),
  get: (id) => api.get(`/entries/${id}`),
  create: (data) => api.post('/entries', data),
  update: (id, data) => api.put(`/entries/${id}`, data),
  delete: (id) => api.delete(`/entries/${id}`),
  updateStatus: (id, status) => api.put(`/entries/${id}/status`, { status }),
  bulkStatus: (entryIds, status) => api.post('/entries/bulk-status', { entryIds, status })
};

// Giveaway entries
export const giveaway = {
  list: (params) => api.get('/giveaway/entries', { params }),
  draw: (month) => api.post('/giveaway/draw', { month }),
  getWinner: (month) => api.get('/giveaway/winner', { params: { month } })
};

// Custom Items
export const customItems = {
  list: (params) => api.get('/custom-items', { params }),
  get: (id) => api.get(`/custom-items/${id}`),
  create: (data) => api.post('/custom-items', data),
  update: (id, data) => api.put(`/custom-items/${id}`, data),
  delete: (id) => api.delete(`/custom-items/${id}`)
};

// Invoices
export const invoices = {
  list: (params) => api.get('/invoices', { params }),
  get: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data, options = {}) => api.put(`/invoices/${id}`, data, options),
  delete: (id) => api.delete(`/invoices/${id}`),
  send: (id) => api.put(`/invoices/${id}/send`),
  paid: (id, amount) => api.put(`/invoices/${id}/paid`, { amount }),
  pdf: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  email: (id) => api.get(`/invoices/${id}/email`),
};

// Reports
export const reports = {
  summary: (params) => api.get('/reports/summary', { params }),
  revenue: (params) => api.get('/reports/revenue', { params }),
  customers: (params) => api.get('/reports/customers', { params }),
  employees: (params) => api.get('/reports/employees', { params }),
  tax: (params) => api.get('/reports/tax', { params }),
  aging: () => api.get('/reports/aging'),
  export: (params) => api.get('/reports/export', { params })
};

// Settings
// Analytics
export const analytics = {
  get: () => api.get('/analytics'),
  getHistory: () => api.get('/analytics/history')
};

export const settings = {
  list: () => api.get('/settings'),
  get: (key) => api.get(`/settings/${key}`),
  update: (data) => api.put('/settings', data),
  updateKey: (key, value) => api.put(`/settings/${key}`, { value })
};

// Arcade Admin (Helfdog's Arcade)
// NOTE: Uses proxy at /api/admin/* (server-side) to avoid CORS and token issues
export const arcadeAdmin = {
  getPendingUsers: () => api.get('/api/admin/pending-users'),
  approveUser: (id) => api.post(`/api/admin/approve/${id}`),
  getAllUsers: () => api.get('/api/admin/users')
};

// Sales Pipeline (deals, replies, bookings, contacts, tasks, sends)
export const sales = {
  // Deals
  listDeals: (params) => api.get('/sales/deals', { params }),
  getDeal: (id) => api.get(`/sales/deals/${id}`),
  createDeal: (data) => api.post('/sales/deals', data),
  updateDeal: (id, data) => api.patch(`/sales/deals/${id}`, data),
  deleteDeal: (id) => api.delete(`/sales/deals/${id}`),
  // Replies
  listReplies: (params) => api.get('/sales/replies', { params }),
  logReply: (data) => api.post('/sales/replies', data),
  // Bookings
  listBookings: (params) => api.get('/sales/bookings', { params }),
  createBooking: (data) => api.post('/sales/bookings', data),
  // Summary
  pipelineSummary: () => api.get('/sales/pipeline-summary'),
  // Contacts (CRM)
  listContacts: (params) => api.get('/sales/contacts', { params }),
  getContact: (id) => api.get(`/sales/contacts/${id}`),
  createContact: (data) => api.post('/sales/contacts', data),
  updateContact: (id, data) => api.patch(`/sales/contacts/${id}`, data),
  deleteContact: (id) => api.delete(`/sales/contacts/${id}`),
  flagContact: (id, do_not_email, reason = null, by = 'matt') =>
    api.post(`/sales/contacts/${id}/flag`, { do_not_email, reason, by }),
  backfillContacts: () => api.post('/sales/contacts/backfill', {}),
  backfillSendHistory: () => api.post('/sales/contacts/backfill-send-history', {}),
  // Tasks
  listTasks: (params) => api.get('/sales/tasks', { params }),
  createTask: (data) => api.post('/sales/tasks', data),
  updateTask: (id, data) => api.patch(`/sales/tasks/${id}`, data),
  deleteTask: (id) => api.delete(`/sales/tasks/${id}`),
  // Sends
  listSends: (params) => api.get('/sales/sends', { params }),
  patchSend: (id, body) => api.patch(`/sales/send/${id}`, body),
  deleteSend: (id) => api.delete(`/sales/send/${id}`),
  // Synchronous dispatch via Graph (bypasses the every-15-min cron window)
  dispatchNow: (id) => api.post(`/sales/send/${id}/dispatch-now`),
  draftsForColdContacts: (body = {}) => api.post('/sales/send/drafts-for-cold-contacts', body),
};

export default api;
